import React, { useState } from "react";
import axios from "axios";
import { useEmailList, useEmailBody, useSendEmail } from "../hooks/emailData";

function Email() {
  const [activeView, setActiveView] = useState("inbox");
  const [selectedEmail, setSelectedEmail] = useState(null);
  const [selectedThread, setSelectedThread] = useState(null);
  const [form, setForm] = useState({
    from: "user1@semionbd.com",
    to: "",
    cc: "",
    subject: "",
    body: ""
  });
  const [status, setStatus] = useState("");

  // API Integration
  const userEmailId = "user1@semionbd.com";
  const { data: emailList, isLoading: emailListLoading, error: emailListError } = useEmailList(userEmailId);
  const { data: emailBody, isLoading: emailBodyLoading } = useEmailBody(userEmailId, selectedEmail?.timestamp);
  const sendEmailMutation = useSendEmail();


  // Debug logging
  console.log("selectedEmail:", selectedEmail);
  console.log("emailBodyLoading:", emailBodyLoading);
  console.log("emailBody:", emailBody);

  // Debug: Log the raw API response
  console.log("Raw emailList from API:", emailList);
  
  // Transform API data to match component structure
  const emails = emailList ? emailList.map((email, index) => {
    console.log(`Email ${index}:`, email);
    console.log(`Email ${index} timestamp:`, email.timestamp);
    
    return {
      id: index + 1,
      thread_id: `thread_${index + 1}`, // Simple thread assignment for now
      sender: email.sender_email,
      sender_name: email.sender_name,
      subject: email.subject,
      body: "", // Will be loaded when clicked
      time: email.timestamp ? new Date(email.timestamp).toLocaleString() : "Unknown time",
      read: false, // Default to unread
      timestamp: email.timestamp
    };
  }) : [];

  // Group emails by thread_id
  const getThreadGroups = () => {
    const threadGroups = {};
    emails.forEach(email => {
      if (!threadGroups[email.thread_id]) {
        threadGroups[email.thread_id] = [];
      }
      threadGroups[email.thread_id].push(email);
    });
    
    // Sort emails within each thread by timestamp (chronological order)
    Object.keys(threadGroups).forEach(threadId => {
      threadGroups[threadId].sort((a, b) => a.timestamp - b.timestamp);
    });
    
    return threadGroups;
  };

  // Get the latest email from each thread for display in inbox
  const getThreadSummaries = () => {
    const threadGroups = getThreadGroups();
    return Object.keys(threadGroups).map(threadId => {
      const threadEmails = threadGroups[threadId];
      const latestEmail = threadEmails[threadEmails.length - 1]; // Most recent email
      const unreadCount = threadEmails.filter(email => !email.read).length;
      
      console.log("Thread summary for", threadId, ":", {
        latestEmail,
        timestamp: latestEmail.timestamp,
        time: latestEmail.time
      });
      
      return {
        thread_id: threadId,
        latest_email: latestEmail,
        email_count: threadEmails.length,
        unread_count: unreadCount,
        has_unread: unreadCount > 0
      };
    }).sort((a, b) => b.latest_email.timestamp - a.latest_email.timestamp); // Sort by most recent
  };

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const sendEmail = async () => {
    try {
      setStatus("Sending...");
      
      // Prepare email data for API
      const emailData = {
        from_email: form.from,
        to_email_list: [form.to, form.cc].filter(email => email.trim() !== ""),
        subject_text: form.subject,
        body_text: form.body
      };

      await sendEmailMutation.mutateAsync(emailData);
      
      setStatus("Email sent successfully!");
      setForm({ from: "user1@semionbd.com", to: "", cc: "", subject: "", body: "" });
      setActiveView("inbox");
    } catch (err) {
      console.error(err);
      setStatus("Failed to send email.");
    }
  };

  const handleThreadClick = (threadSummary) => {
    setSelectedThread(threadSummary);
    setSelectedEmail(null);
  };

  const handleEmailClick = (email) => {
    console.log("=== EMAIL CLICKED ===");
    console.log("Email clicked:", email);
    console.log("Email timestamp:", email.timestamp);
    console.log("Current selectedEmail before set:", selectedEmail);
    
    setSelectedEmail(email);
    console.log("selectedEmail state set to:", email);
    console.log("=== END EMAIL CLICK ===");
    // Email body will be automatically loaded via useEmailBody hook
  };

  const handleReply = () => {
    setForm({
      from: "user1@semionbd.com",
      to: selectedEmail.sender,
      cc: "",
      subject: `Re: ${selectedEmail.subject}`,
      body: `\n\n--- Original Message ---\nFrom: ${selectedEmail.sender}\nSubject: ${selectedEmail.subject}\n\n${emailBody || "Loading..."}`
    });
    setActiveView("compose");
  };

  const handleForward = () => {
    setForm({
      from: "user1@semionbd.com",
      to: "",
      cc: "",
      subject: `Fwd: ${selectedEmail.subject}`,
      body: `\n\n--- Forwarded Message ---\nFrom: ${selectedEmail.sender}\nSubject: ${selectedEmail.subject}\n\n${emailBody || "Loading..."}`
    });
    setActiveView("compose");
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="pt-safe-area-top">
        <div className="flex h-screen">
          {/* Left Sidebar */}
          <div className="w-64 bg-white border-r border-gray-200 flex flex-col">
            <div className="p-4 border-b border-gray-200">
              <h1 className="text-xl font-bold text-gray-800">Email Client</h1>
            </div>
            
            <nav className="flex-1 p-4">
              <ul className="space-y-2">
                <li>
                  <button
                    onClick={() => setActiveView("compose")}
                    className={`w-full text-left px-4 py-2 rounded-md transition-colors ${
                      activeView === "compose" ? "bg-blue-100 text-blue-700" : "text-gray-700 hover:bg-gray-100"
                    }`}
                  >
                    ✉️ Compose
                  </button>
                </li>
                <li>
                  <button
                    onClick={() => setActiveView("inbox")}
                    className={`w-full text-left px-4 py-2 rounded-md transition-colors ${
                      activeView === "inbox" ? "bg-blue-100 text-blue-700" : "text-gray-700 hover:bg-gray-100"
                    }`}
                  >
                    📥 Inbox ({emails.filter(e => !e.read).length})
                  </button>
                </li>
                <li>
                  <button
                    onClick={() => setActiveView("sent")}
                    className={`w-full text-left px-4 py-2 rounded-md transition-colors ${
                      activeView === "sent" ? "bg-blue-100 text-blue-700" : "text-gray-700 hover:bg-gray-100"
                    }`}
                  >
                    📤 Sent
                  </button>
                </li>
              </ul>
            </nav>
          </div>

          {/* Main Content Area */}
          <div className="flex-1 flex flex-col">
            {activeView === "compose" && (
              <div className="flex-1 p-6">
                <div className="bg-white rounded-lg shadow-md p-6 max-w-4xl">
                  <h2 className="text-2xl font-bold text-gray-800 mb-6">Compose Email</h2>
                  
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">From:</label>
                      <input
                        name="from"
                        value={form.from}
                        onChange={handleChange}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="your.email@example.com"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">To:</label>
                      <input
                        name="to"
                        value={form.to}
                        onChange={handleChange}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="recipient@example.com"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">CC:</label>
                      <input
                        name="cc"
                        value={form.cc}
                        onChange={handleChange}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="cc@example.com"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Subject:</label>
                      <input
                        name="subject"
                        value={form.subject}
                        onChange={handleChange}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="Email subject"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Body:</label>
                      <textarea
                        name="body"
                        value={form.body}
                        onChange={handleChange}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        rows={10}
                        placeholder="Type your message here..."
                      />
                    </div>
                    
                    <div className="flex space-x-4">
                      <button
                        onClick={sendEmail}
                        className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
                      >
                        Send
                      </button>
                      <button
                        onClick={() => setActiveView("inbox")}
                        className="px-6 py-2 bg-gray-500 text-white rounded-md hover:bg-gray-600 transition-colors"
                      >
                        Cancel
                      </button>
                    </div>
                    
                    {status && (
                      <div className={`p-3 rounded-md ${status.includes("success") ? "bg-green-100 text-green-800" : "bg-red-100 text-red-800"}`}>
                        {status}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}

            {activeView === "inbox" && (
              <div className="flex-1 flex">
                {/* Email List */}
                <div className="flex-1 border-r border-gray-200">
                  <div className="p-4 border-b border-gray-200">
                    <h2 className="text-xl font-semibold text-gray-800">Inbox</h2>
                  </div>
                  <div className="overflow-y-auto">
                    {emailListLoading ? (
                      <div className="p-4 text-center text-gray-500">Loading emails...</div>
                    ) : emailListError ? (
                      <div className="p-4 text-center text-red-500">Error loading emails: {emailListError.message}</div>
                    ) : getThreadSummaries().length === 0 ? (
                      <div className="p-4 text-center text-gray-500">No emails found</div>
                    ) : (
                      getThreadSummaries().map((threadSummary) => (
                      <div
                        key={threadSummary.thread_id}
                        onClick={() => handleEmailClick(threadSummary.latest_email)}
                        className={`p-4 border-b border-gray-100 cursor-pointer hover:bg-gray-50 ${
                          threadSummary.has_unread ? "bg-blue-50" : ""
                        }`}
                      >
                        <div className="flex justify-between items-center">
                          <div className="flex-1 flex items-center space-x-3">
                            <div className="flex items-center space-x-2">
                              <span className="font-medium text-gray-900">{threadSummary.latest_email.sender}</span>
                              {threadSummary.has_unread && <div className="w-2 h-2 bg-blue-500 rounded-full"></div>}
                              {threadSummary.email_count > 1 && (
                                <span className="text-xs bg-gray-200 text-gray-600 px-2 py-1 rounded-full">
                                  {threadSummary.email_count}
                                </span>
                              )}
                            </div>
                            <div className="text-gray-800 font-medium">{threadSummary.latest_email.subject}</div>
                          </div>
                          <div className="text-gray-500 text-sm ml-4">{threadSummary.latest_email.time}</div>
                        </div>
                      </div>
                    ))
                    )}
                  </div>
                </div>

                {/* Thread View */}
                {selectedThread && !selectedEmail && (
                  <div className="w-1/2 p-6 bg-white">
                    <div className="mb-4">
                      <div className="flex justify-between items-start mb-4">
                        <h3 className="text-lg font-semibold text-gray-800">
                          {selectedThread.latest_email.subject}
                        </h3>
                        <button
                          onClick={() => setSelectedThread(null)}
                          className="px-3 py-1 bg-gray-500 text-white text-sm rounded hover:bg-gray-600"
                        >
                          Back to Inbox
                        </button>
                      </div>
                      <div className="text-sm text-gray-600 mb-4">
                        <div>Thread: {selectedThread.email_count} messages</div>
                        {selectedThread.unread_count > 0 && (
                          <div className="text-blue-600">{selectedThread.unread_count} unread</div>
                        )}
                      </div>
                    </div>
                    <div className="space-y-4">
                      {getThreadGroups()[selectedThread.thread_id].map((email, index) => (
                        <div
                          key={email.id}
                          onClick={() => handleEmailClick(email)}
                          className={`p-4 border border-gray-200 rounded-lg cursor-pointer hover:bg-gray-50 ${
                            !email.read ? "bg-blue-50 border-blue-200" : ""
                          }`}
                        >
                          <div className="flex justify-between items-start mb-2">
                            <div className="flex items-center space-x-2">
                              <span className="font-medium text-gray-900">{email.sender}</span>
                              {!email.read && <div className="w-2 h-2 bg-blue-500 rounded-full"></div>}
                            </div>
                            <div className="text-gray-500 text-sm">{email.time}</div>
                          </div>
                          <div className="text-gray-800 text-sm whitespace-pre-wrap">{email.body}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Email Detail */}
                {selectedEmail && (
                  <div className="w-1/2 p-6 bg-white">
                    <div className="mb-4">
                      <div className="flex justify-between items-start mb-2">
                        <h3 className="text-lg font-semibold text-gray-800">{selectedEmail.subject}</h3>
                        <div className="flex space-x-2">
                          <button
                            onClick={() => {
                              setSelectedEmail(null);
                              setSelectedThread(null);
                            }}
                            className="px-3 py-1 bg-gray-500 text-white text-sm rounded hover:bg-gray-600"
                          >
                            Back to Inbox
                          </button>
                          <button
                            onClick={handleReply}
                            className="px-3 py-1 bg-blue-600 text-white text-sm rounded hover:bg-blue-700"
                          >
                            Reply
                          </button>
                          <button
                            onClick={handleForward}
                            className="px-3 py-1 bg-gray-600 text-white text-sm rounded hover:bg-gray-700"
                          >
                            Forward
                          </button>
                        </div>
                      </div>
                      <div className="text-sm text-gray-600 mb-4">
                        <div>From: {selectedEmail.sender}</div>
                        <div>Time: {selectedEmail.time}</div>
                      </div>
                    </div>
                    <div className="border-t border-gray-200 pt-4">
                      {emailBodyLoading ? (
                        <div className="text-gray-500">Loading email content...</div>
                      ) : emailBody ? (
                        <div className="text-gray-800 whitespace-pre-wrap">{emailBody}</div>
                      ) : (
                        <div className="text-gray-500">No content available</div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            )}

            {activeView === "sent" && (
              <div className="flex-1 p-6">
                <div className="bg-white rounded-lg shadow-md p-6">
                  <h2 className="text-xl font-semibold text-gray-800 mb-4">Sent Emails</h2>
                  <p className="text-gray-600">No sent emails yet.</p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default Email;
